/**
 * Aspect — Structure selection (overhead marquee -> wall/door/window IDs)
 * --------------------------------------------------------------
 * Active only in the overhead scene-view. Left-drag paints a selection
 * rectangle (Windows-style); on release we project every wall (endpoints +
 * midpoint) and every opening (center) to screen and select those that fall
 * inside the rectangle. Selected elements are keyed by their STABLE topology
 * ids (`edgeId` for walls, `sourceId` for openings) so the AI refine pass can
 * patch exactly those elements in `spec.graph`.
 *
 * Emits:
 *   - `structure:rect`              { active, x0, y0, x1, y1 }  (page px; for the overlay)
 *   - `structure:selection-changed` { elements: [{ id, kind, label }] }
 * Listens:
 *   - `structure:deselect` { id }, `structure:clear`
 *   - `cam:mode-changed`, `scene:room-loaded`
 */

import * as pc from "playcanvas";

const DRAG_THRESHOLD = 4; // px before a marquee starts drawing
const CLICK_SLOP = 7; // total px movement still treated as a click (not a drag)
const HL_COLOR = new pc.Color(0.1, 0.95, 1.0); // cyan outline
const OUTLINE_THICKNESS = 0.05; // metres — bar thickness of the wireframe cage
// Pulsing-glow animation: opacity + emissive breathe between these bounds.
const GLOW_SPEED = 3.2;
const GLOW_OPACITY_MIN = 0.12;
const GLOW_OPACITY_MAX = 1.0;
const GLOW_EMISSIVE_MIN = 0.55;
const GLOW_EMISSIVE_MAX = 1.7;

export class StructureSelect {
  /**
   * @param {pc.AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   * @param {{ canvas?: HTMLCanvasElement }} [opts]
   */
  constructor(app, camera, scene, opts = {}) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.canvas = opts.canvas ?? app.graphicsDevice?.canvas ?? null;

    this._overhead = false;
    this._dragging = false;
    this._moved = false;
    this._start = { x: 0, y: 0 };
    this._cur = { x: 0, y: 0 };
    this._shift = false;
    this._rafPending = false;
    /** Selection snapshot at drag start (so Shift extends it live). */
    this._baseSelected = new Map();

    /** @type {Map<string, {id,kind,label}>} keyed by stable id */
    this._selected = new Map();

    this._scratch = new pc.Vec3();
    this._hlRoot = new pc.Entity("structure-highlights");
    this.app.root.addChild(this._hlRoot);
    this._hlMat = this._makeMat();

    this._onDown = (e) => this._mouseDown(e);
    this._onMove = (e) => this._mouseMove(e);
    this._onUp = (e) => this._mouseUp(e);

    if (this.canvas) this.canvas.addEventListener("mousedown", this._onDown);
    window.addEventListener("mousemove", this._onMove);
    window.addEventListener("mouseup", this._onUp);

    this._glowT = 0;
    this.app.on("update", (dt) => this._animate(dt));
    this.app.on("cam:mode-changed", ({ mode }) => {
      this._overhead = mode === "overhead";
      if (!this._overhead) this.clear();
    });
    this.app.on("scene:room-loaded", () => this.clear());
    this.app.on("structure:deselect", ({ id }) => this._deselect(id));
    this.app.on("structure:clear", () => this.clear());
  }

  _makeMat() {
    const mat = new pc.StandardMaterial();
    mat.useLighting = false; // flat, pure colour
    mat.diffuse = new pc.Color(0, 0, 0);
    mat.emissive = HL_COLOR;
    mat.emissiveIntensity = GLOW_EMISSIVE_MAX;
    mat.opacity = GLOW_OPACITY_MAX;
    // Alpha-blended so the glow can fade toward transparent cyan.
    mat.blendType = pc.BLEND_NORMAL;
    // Draw the outline on top of everything so the selection is always obvious,
    // even through walls / when walls fade in overhead cutaway mode.
    mat.depthTest = false;
    mat.depthWrite = false;
    mat.update();
    return mat;
  }

  /** Smooth, fluid glow: pulse opacity + emissive between normal and faded cyan. */
  _animate(dt) {
    if (this._selected.size === 0) return;
    this._glowT = (this._glowT ?? 0) + dt;
    const phase = (Math.sin(this._glowT * GLOW_SPEED) + 1) / 2; // 0..1, smooth
    const mat = this._hlMat;
    mat.opacity = GLOW_OPACITY_MIN + (GLOW_OPACITY_MAX - GLOW_OPACITY_MIN) * phase;
    mat.emissiveIntensity = GLOW_EMISSIVE_MIN + (GLOW_EMISSIVE_MAX - GLOW_EMISSIVE_MIN) * phase;
    mat.update();
  }

  _floorY() {
    return this.scene.room?.floorY ?? 0;
  }

  _roomHeight() {
    return this.scene.room?.dimensions?.height ?? 2.7;
  }

  // --- Marquee input -----------------------------------------------------

  _mouseDown(e) {
    if (!this._overhead) return;
    // Phones use drag/pinch for the overhead camera — skip marquee editing.
    if (typeof document !== "undefined" && document.body.classList.contains("touch-ui")) return;
    if (e.button === 0) {
      this._dragging = true;
      this._moved = false;
      // Always accumulate onto the existing selection — picking a new element
      // never drops the previously selected ones (removal is explicit).
      this._baseSelected = new Map(this._selected);
      this._start = { x: e.clientX, y: e.clientY };
      this._cur = { ...this._start };
    } else if (e.button === 2) {
      // Right button: track for a deselect tap (a drag is camera-look instead).
      this._rDown = { x: e.clientX, y: e.clientY };
      this._rMoved = false;
    }
  }

  _mouseMove(e) {
    if (this._rDown && !this._rMoved) {
      if (Math.hypot(e.clientX - this._rDown.x, e.clientY - this._rDown.y) > DRAG_THRESHOLD) this._rMoved = true;
    }
    if (!this._dragging) return;
    this._cur = { x: e.clientX, y: e.clientY };
    if (!this._moved && Math.hypot(this._cur.x - this._start.x, this._cur.y - this._start.y) > DRAG_THRESHOLD) {
      this._moved = true;
    }
    if (!this._moved) return;
    this.app.fire("structure:rect", {
      active: true,
      x0: this._start.x,
      y0: this._start.y,
      x1: this._cur.x,
      y1: this._cur.y,
    });
    this._scheduleLiveSelect(); // selection updates in real time as you drag
  }

  _mouseUp(e) {
    // Right-click tap (no camera-look drag) removes the element under the cursor.
    if (e.button === 2 && this._rDown) {
      const tap = !this._rMoved;
      this._rDown = null;
      if (tap && this._overhead) this._rightClickDeselect(e.clientX, e.clientY);
      return;
    }
    if (!this._dragging) return;
    this._dragging = false;
    this.app.fire("structure:rect", { active: false });
    // A short drag (or jittery click) counts as a click so single-click always
    // works; only a real marquee uses the rectangle.
    const dist = Math.hypot(this._cur.x - this._start.x, this._cur.y - this._start.y);
    if (this._moved && dist >= CLICK_SLOP) {
      this._liveSelect(); // commit the final rectangle
    } else {
      this._clickSelect(this._start.x, this._start.y); // single-click adds
    }
  }

  /** Nearest selectable element under a screen point, or null. */
  _pickAt(px, py) {
    const room = this.scene.room;
    if (!room) return null;
    const RADIUS = 26; // px tolerance for a click pick
    let best = null;
    let bestD = RADIUS;

    const consider = (el, pts) => {
      let d = Infinity;
      for (const p of pts) {
        const dist = Math.hypot(p.x - px, p.y - py);
        if (dist < d) d = dist;
      }
      // Prefer openings slightly (they sit on a wall) by a small bias.
      const biased = el.kind === "wall" ? d : d - 6;
      if (biased < bestD) {
        bestD = biased;
        best = el;
      }
    };

    for (const w of room.walls ?? []) {
      const id = String(w.edgeId ?? w.id);
      consider({ id, kind: "wall", label: `Wall ${id}` }, this._wallScreenPoints(w));
    }
    for (const op of room.openings ?? []) {
      const id = String(op.sourceId ?? op.id);
      const kind = op.type === "window" ? "window" : "door";
      consider({ id, kind, label: `${kind === "window" ? "Window" : "Door"} ${id}` }, this._openingScreenPoints(op));
    }
    if (best) return best;

    // Floor / ceiling: clicked over open interior (no wall/opening hit).
    const poly = this._floorPolyWorld();
    if (poly) {
      const proj = poly.map(([x, z]) => this._project(x, this._floorY(), z)).filter(Boolean);
      if (proj.length >= 3 && this._pointInProjPoly(px, py, proj)) {
        return { kind: "floor", id: "floor", label: "Floor" };
      }
    }
    return null;
  }

  /** Single left-click: ADD the element under the cursor (keep prior selection). */
  _clickSelect(px, py) {
    const hit = this._pickAt(px, py);
    if (!hit) return; // clicking empty space leaves the selection untouched
    if (hit.kind === "floor" || hit.kind === "ceiling") {
      this._selected.set("floor", { id: "floor", kind: "floor", label: "Floor" });
      this._selected.set("ceiling", { id: "ceiling", kind: "ceiling", label: "Ceiling" });
    } else {
      this._selected.set(hit.id, hit);
    }
    this._emit();
  }

  /** Right-click: remove the element under the cursor (empty space clears all). */
  _rightClickDeselect(px, py) {
    const hit = this._pickAt(px, py);
    if (!hit) {
      this.clear();
      return;
    }
    if (hit.kind === "floor" || hit.kind === "ceiling") {
      this._selected.delete("floor");
      this._selected.delete("ceiling");
    } else {
      this._selected.delete(hit.id);
    }
    this._emit();
  }

  /** Coalesce rapid mousemove events into one selection pass per frame. */
  _scheduleLiveSelect() {
    if (this._rafPending) return;
    this._rafPending = true;
    requestAnimationFrame(() => {
      this._rafPending = false;
      if (this._dragging && this._moved) this._liveSelect();
    });
  }

  _liveSelect() {
    const x0 = Math.min(this._start.x, this._cur.x);
    const y0 = Math.min(this._start.y, this._cur.y);
    const x1 = Math.max(this._start.x, this._cur.x);
    const y1 = Math.max(this._start.y, this._cur.y);
    const hits = this._computeHits(x0, y0, x1, y1);
    const next = new Map(this._baseSelected);
    for (const [id, el] of hits) next.set(id, el);
    this._selected = next;
    this._emit();
  }

  // --- Hit testing -------------------------------------------------------

  _project(x, y, z) {
    const sv = this.camera.camera.worldToScreen(this._scratch.set(x, y, z), this._scratch);
    if (sv.z <= 0) return null;
    const rect = this.canvas?.getBoundingClientRect();
    return { x: sv.x + (rect?.left ?? 0), y: sv.y + (rect?.top ?? 0) };
  }

  _inRect(p, x0, y0, x1, y1) {
    return p && p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1;
  }

  /** Floor outline in world [x,z], from the room polygon or its bounds. */
  _floorPolyWorld() {
    const room = this.scene.room;
    if (Array.isArray(room?.floorPolygon) && room.floorPolygon.length >= 3) return room.floorPolygon;
    const b = room?.bounds;
    if (!b) return null;
    return [
      [b.min[0], b.min[2]],
      [b.max[0], b.min[2]],
      [b.max[0], b.max[2]],
      [b.min[0], b.max[2]],
    ];
  }

  /** Screen-space point-in-polygon (ray cast). `poly` is [{x,y}, …]. */
  _pointInProjPoly(px, py, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x;
      const yi = poly[i].y;
      const xj = poly[j].x;
      const yj = poly[j].y;
      const hit = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi || 1e-9) + xi;
      if (hit) inside = !inside;
    }
    return inside;
  }

  /** Heights to sample a wall/opening at, so any visible part is hittable. */
  _heights() {
    const fy = this._floorY();
    const h = this._roomHeight();
    return [fy + 0.15, fy + h * 0.5, fy + h - 0.15];
  }

  /** Projected screen points sampled across a wall's full surface. */
  _wallScreenPoints(w) {
    const ax = w.start[0];
    const az = w.start[1];
    const bx = w.end[0];
    const bz = w.end[1];
    const len = Math.hypot(bx - ax, bz - az) || 0.1;
    const n = Math.min(120, Math.max(3, Math.ceil(len / 0.12)));
    const pts = [];
    for (const y of this._heights()) {
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        const p = this._project(ax + (bx - ax) * t, y, az + (bz - az) * t);
        if (p) pts.push(p);
      }
    }
    return pts;
  }

  /** Projected screen points covering an opening's panel (width × height). */
  _openingScreenPoints(op) {
    const c = op.center;
    const dir = op.dir ?? [1, 0];
    const half = (op.width ?? 0.9) / 2;
    const h = op.height ?? 2.0;
    const ys = [c[1] - h / 2 + 0.05, c[1], c[1] + h / 2 - 0.05];
    const pts = [];
    for (const y of ys) {
      for (const f of [-1, -0.5, 0, 0.5, 1]) {
        const p = this._project(c[0] + dir[0] * half * f, y, c[2] + dir[1] * half * f);
        if (p) pts.push(p);
      }
    }
    return pts;
  }

  _wallHitsRect(w, x0, y0, x1, y1) {
    return this._wallScreenPoints(w).some((p) => this._inRect(p, x0, y0, x1, y1));
  }

  _openingHitsRect(op, x0, y0, x1, y1) {
    return this._openingScreenPoints(op).some((p) => this._inRect(p, x0, y0, x1, y1));
  }

  /** Pure hit-test: returns the elements whose geometry overlaps the rect. */
  _computeHits(x0, y0, x1, y1) {
    const room = this.scene.room;
    const found = new Map();
    if (!room) return found;
    const floorY = this._floorY();

    // Walls — selectable on ANY home (graph or default). Refine uses the stable
    // graph edgeId when present; otherwise the built wall id is still selectable.
    for (const w of room.walls ?? []) {
      if (this._wallHitsRect(w, x0, y0, x1, y1)) {
        const id = String(w.edgeId ?? w.id);
        found.set(id, { id, kind: "wall", label: `Wall ${id}` });
      }
    }

    // Doors (incl. double doors) + windows.
    for (const op of room.openings ?? []) {
      if (this._openingHitsRect(op, x0, y0, x1, y1)) {
        const id = String(op.sourceId ?? op.id);
        const kind = op.type === "window" ? "window" : "door";
        const label = `${kind === "window" ? "Window" : "Door"} ${id}`;
        found.set(id, { id, kind, label });
      }
    }

    // Floor + ceiling — selected when the marquee is dragged over open interior
    // (no wall/opening targeted), so wall/door picks stay precise.
    if (found.size === 0) {
      const poly = this._floorPolyWorld();
      if (poly) {
        const cx = (x0 + x1) / 2;
        const cy = (y0 + y1) / 2;
        const projFloor = poly.map(([x, z]) => this._project(x, floorY, z)).filter(Boolean);
        if (projFloor.length >= 3 && this._pointInProjPoly(cx, cy, projFloor)) {
          found.set("floor", { id: "floor", kind: "floor", label: "Floor" });
          found.set("ceiling", { id: "ceiling", kind: "ceiling", label: "Ceiling" });
        }
      }
    }

    return found;
  }

  _deselect(id) {
    if (this._selected.delete(String(id))) this._emit();
  }

  clear() {
    if (this._selected.size === 0 && this._hlRoot.children.length === 0) return;
    this._selected.clear();
    this._emit();
  }

  _emit() {
    this._rebuildHighlights();
    this.app.fire("structure:selection-changed", { elements: [...this._selected.values()] });
  }

  // --- 3D highlight ------------------------------------------------------

  _rebuildHighlights() {
    for (const c of [...this._hlRoot.children]) c.destroy();
    const room = this.scene.room;
    if (!room) return;

    const wallById = new Map();
    for (const w of room.walls ?? []) wallById.set(String(w.edgeId ?? w.id), w);
    const openBySrc = new Map();
    for (const op of room.openings ?? []) {
      const sid = op.sourceId ?? op.id;
      if (sid) openBySrc.set(String(sid), op);
    }

    for (const el of this._selected.values()) {
      if (el.kind === "wall") {
        const w = wallById.get(el.id);
        if (w) this._addWallOutline(w);
      } else if (el.kind === "floor" || el.kind === "ceiling") {
        this._addSurfaceOutline(el.kind);
      } else {
        const op = openBySrc.get(el.id);
        if (op) this._addOpeningOutline(op);
      }
    }
  }

  /**
   * Build a hollow wireframe "cage" (12 thin bars) of size w×h×d. A real outline
   * wraps the object instead of filling it with a translucent box.
   */
  _outlineCage(w, h, d) {
    const t = OUTLINE_THICKNESS;
    const hx = w / 2;
    const hy = h / 2;
    const hz = d / 2;
    const parent = new pc.Entity("struct-outline");
    const bar = (sx, sy, sz, px, py, pz) => {
      const e = new pc.Entity("bar");
      e.addComponent("render", { type: "box", material: this._hlMat, castShadows: false });
      e.setLocalScale(sx, sy, sz);
      e.setLocalPosition(px, py, pz);
      parent.addChild(e);
    };
    // 4 bars along X, 4 along Y, 4 along Z — the edges of the box.
    for (const sy of [-hy, hy]) for (const sz of [-hz, hz]) bar(w + t, t, t, 0, sy, sz);
    for (const sx of [-hx, hx]) for (const sz of [-hz, hz]) bar(t, h + t, t, sx, 0, sz);
    for (const sx of [-hx, hx]) for (const sy of [-hy, hy]) bar(t, t, d + t, sx, sy, 0);
    return parent;
  }

  _addCage(w, h, d, cx, cy, cz, yawDeg) {
    const cage = this._outlineCage(w, h, d);
    cage.setLocalEulerAngles(0, yawDeg, 0);
    cage.setLocalPosition(cx, cy, cz);
    this._hlRoot.addChild(cage);
  }

  _addWallOutline(w) {
    const ax = w.start[0];
    const az = w.start[1];
    const bx = w.end[0];
    const bz = w.end[1];
    const len = Math.hypot(bx - ax, bz - az) || 0.1;
    const yaw = -Math.atan2(bz - az, bx - ax) * (180 / Math.PI);
    const h = this._roomHeight();
    const y = this._floorY() + h / 2;
    this._addCage(len + 0.06, h * 0.99, (w.thickness ?? 0.12) + 0.12, (ax + bx) / 2, y, (az + bz) / 2, yaw);
  }

  /** Thin bar between two world points (for flat rectangular frames). */
  _addEdgeBar(parent, ax, ay, az, bx, by, bz) {
    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const len = Math.hypot(dx, dy, dz) || 1e-6;
    const t = OUTLINE_THICKNESS;
    const e = new pc.Entity("bar");
    e.addComponent("render", { type: "box", material: this._hlMat, castShadows: false });
    e.setLocalPosition((ax + bx) / 2, (ay + by) / 2, (az + bz) / 2);

    // Vertical edge (window/door sides).
    if (Math.abs(dy) >= Math.abs(dx) && Math.abs(dy) >= Math.abs(dz)) {
      e.setLocalScale(t, len + t, t);
      return parent.addChild(e);
    }
    // Horizontal edge along the wall (top/bottom of opening).
    const yaw = (-Math.atan2(dz, dx) * 180) / Math.PI;
    e.setLocalScale(len + t, t, t);
    e.setLocalEulerAngles(0, yaw, 0);
    parent.addChild(e);
  }

  /**
   * Flat 4-edge frame in the wall plane — wraps the opening like a picture frame,
   * not a 3D box that reads as a prism through the glass.
   */
  _addOpeningOutline(op) {
    const wall = (this.scene.room?.walls ?? []).find((w) => String(w.id) === String(op.wall));
    const dir = op.dir ?? wall?.dir ?? [1, 0];
    const normal = op.normal ?? wall?.normal ?? [0, 1];
    const dLen = Math.hypot(dir[0], dir[1]) || 1;
    const ux = dir[0] / dLen;
    const uz = dir[1] / dLen;
    const nx = normal[0];
    const nz = normal[1];

    const cx = op.center[0];
    const cy = op.center[1];
    const cz = op.center[2];
    const hw = (op.width ?? 0.9) / 2 + 0.04;
    const hh = (op.height ?? 2.0) / 2 + 0.04;
    // Sit on the visible window/door face (same inset as room_builder panes).
    const inset = 0.1;
    const ox = cx + nx * inset;
    const oy = cy;
    const oz = cz + nz * inset;

    const pt = (along, up) => [ox + ux * along, oy + up, oz + uz * along];
    const bl = pt(-hw, -hh);
    const br = pt(hw, -hh);
    const tl = pt(-hw, hh);
    const tr = pt(hw, hh);

    const frame = new pc.Entity("struct-frame");
    this._addEdgeBar(frame, ...bl, ...br);
    this._addEdgeBar(frame, ...tl, ...tr);
    this._addEdgeBar(frame, ...bl, ...tl);
    this._addEdgeBar(frame, ...br, ...tr);
    this._hlRoot.addChild(frame);
  }

  _addSurfaceOutline(kind) {
    const b = this.scene.room?.bounds;
    if (!b) return;
    const w = b.max[0] - b.min[0];
    const d = b.max[2] - b.min[2];
    const cx = (b.min[0] + b.max[0]) / 2;
    const cz = (b.min[2] + b.max[2]) / 2;
    const y = kind === "ceiling" ? this._floorY() + this._roomHeight() : this._floorY();
    this._addCage(w, 0.06, d, cx, y, cz, 0);
  }

  destroy() {
    if (this.canvas) this.canvas.removeEventListener("mousedown", this._onDown);
    window.removeEventListener("mousemove", this._onMove);
    window.removeEventListener("mouseup", this._onUp);
    this._hlRoot?.destroy();
    this._hlMat?.destroy();
  }
}

export default StructureSelect;
